import * as fs from 'fs/promises';
import type { TSchema } from '@sinclair/typebox';
import type { FormatName } from '@json-to-office/jto-ops';

interface VersionedPropsSchema {
  version: string;
  propsSchema: TSchema;
  hasChildren?: boolean;
}

interface CustomComponentSchema {
  name: string;
  propsSchema: TSchema;
  hasChildren?: boolean;
  versionedProps?: VersionedPropsSchema[];
}

type JsonSchema = Record<string, unknown>;

const STRUCTURAL_KEYS = new Set([
  'type',
  'properties',
  'required',
  'items',
  'anyOf',
  'allOf',
  'oneOf',
  'const',
  'enum',
  '$ref',
  'patternProperties',
  'not',
  // Ref targets are inlined at export time, so carrying the containers into the
  // emitted options would leave inert duplicates behind.
  '$defs',
  'definitions',
]);

/**
 * Exports executable TypeBox source without weakening schemas to Type.Any().
 * Unified document files call the same registry-backed generator as runtime;
 * split component files reconstruct their supplied props schema with TypeBox.
 */
export class TypeBoxExporter {
  private formatName: FormatName;
  /** Root of the schema currently being converted, for `$ref` resolution. */
  private refRoot: JsonSchema | undefined;
  /** Pointers being inlined, so a recursive `$ref` is reported not looped. */
  private refStack: string[] = [];

  constructor(formatName: FormatName = 'docx') {
    this.formatName = formatName;
  }

  private get sharedPackage(): string {
    return this.formatName === 'docx'
      ? '@json-to-office/shared-docx'
      : '@json-to-office/shared-pptx';
  }

  async exportDocumentSchema(
    schema: TSchema,
    outputPath: string,
    customComponents: CustomComponentSchema[]
  ): Promise<void> {
    const hasCustomComponents = customComponents.length > 0;
    const imports = hasCustomComponents
      ? "import { Type, type Static } from '@sinclair/typebox';"
      : "import { type Static } from '@sinclair/typebox';";
    const customSchemaDeclarations =
      this.renderCustomSchemaDeclarations(customComponents);
    const generatorOptions = this.renderGeneratorOptions(
      schema,
      customComponents
    );

    const content = [
      '/**',
      ` * Generated TypeBox document schema (${this.formatName.toUpperCase()}).`,
      hasCustomComponents
        ? ' * Includes standard components and custom plugin components.'
        : ' * Includes standard components only.',
      ' * Generated by json-to-office. Do not edit by hand.',
      ' */',
      '',
      imports,
      `import { generateUnifiedDocumentSchema } from '${this.sharedPackage}';`,
      customSchemaDeclarations,
      `export const DocumentSchema = generateUnifiedDocumentSchema(${generatorOptions});`,
      '',
      'export type DocumentType = Static<typeof DocumentSchema>;',
      '',
    ]
      .filter((line, index, lines) => {
        return line !== '' || (index > 0 && lines[index - 1] !== '');
      })
      .join('\n');

    await fs.writeFile(outputPath, content, 'utf-8');
  }

  async exportThemeSchema(_schema: TSchema, outputPath: string): Promise<void> {
    const content = [
      '/**',
      ` * Generated TypeBox theme schema (${this.formatName.toUpperCase()}).`,
      ' * Generated by json-to-office. Do not edit by hand.',
      ' */',
      '',
      "import { type Static } from '@sinclair/typebox';",
      '',
      `export { ThemeConfigSchema } from '${this.sharedPackage}';`,
      '',
      `import { ThemeConfigSchema } from '${this.sharedPackage}';`,
      'export type ThemeType = Static<typeof ThemeConfigSchema>;',
      '',
    ].join('\n');

    await fs.writeFile(outputPath, content, 'utf-8');
  }

  async exportComponentSchema(
    componentName: string,
    schema: TSchema,
    outputPath: string,
    metadata?: { title?: string; description?: string }
  ): Promise<void> {
    const pascalName = this.toPascalCase(componentName);
    const propsSchema = this.schemaToTypeBox(schema);

    const content = [
      '/**',
      ` * Generated TypeBox component schema: ${componentName}.`,
      metadata?.description ? ` * ${metadata.description}` : '',
      ' * Generated by json-to-office. Do not edit by hand.',
      ' */',
      '',
      "import { Type, type Static } from '@sinclair/typebox';",
      '',
      `export const ${pascalName}PropsSchema = ${propsSchema};`,
      '',
      `export const ${pascalName}ComponentSchema = Type.Object(`,
      '  {',
      `    name: Type.Literal(${JSON.stringify(componentName)}),`,
      '    id: Type.Optional(Type.String()),',
      `    props: ${pascalName}PropsSchema,`,
      '  },',
      '  { additionalProperties: false }',
      ');',
      '',
      `export type ${pascalName}ComponentType = Static<typeof ${pascalName}ComponentSchema>;`,
      '',
    ]
      .filter((line) => line !== '')
      .join('\n');

    await fs.writeFile(outputPath, content, 'utf-8');
  }

  private renderGeneratorOptions(
    schema: TSchema,
    customComponents: CustomComponentSchema[]
  ): string {
    if (this.formatName === 'pptx') {
      const components = customComponents.map((component, index) => {
        const versions = component.versionedProps?.length
          ? component.versionedProps.map((entry, versionIndex) => ({
              version: entry.version,
              schemaName: this.customSchemaName(index, versionIndex),
              hasChildren: entry.hasChildren,
            }))
          : [
              {
                version: '1.0.0',
                schemaName: this.customSchemaName(index),
                hasChildren: component.hasChildren,
              },
            ];

        return [
          '    {',
          `      name: ${JSON.stringify(component.name)},`,
          '      versions: [',
          ...versions.flatMap((entry) => [
            '        {',
            `          version: ${JSON.stringify(entry.version)},`,
            `          propsSchema: ${entry.schemaName},`,
            ...(entry.hasChildren === undefined
              ? []
              : [`          hasChildren: ${entry.hasChildren},`]),
            '        },',
          ]),
          '      ],',
          '    },',
        ].join('\n');
      });

      return ['{', '  customComponents: [', ...components, '  ],', '}'].join(
        '\n'
      );
    }

    const components = customComponents.map((component, index) => {
      const versionedProps = component.versionedProps?.length
        ? [
            '      versionedProps: [',
            ...component.versionedProps.flatMap((entry, versionIndex) => [
              '        {',
              `          version: ${JSON.stringify(entry.version)},`,
              `          propsSchema: ${this.customSchemaName(index, versionIndex)},`,
              ...(entry.hasChildren === undefined
                ? []
                : [`          hasChildren: ${entry.hasChildren},`]),
              '        },',
            ]),
            '      ],',
          ]
        : [];

      return [
        '    {',
        `      name: ${JSON.stringify(component.name)},`,
        `      propsSchema: ${this.customSchemaName(index)},`,
        ...(component.hasChildren === undefined
          ? []
          : [`      hasChildren: ${component.hasChildren},`]),
        ...versionedProps,
        '    },',
      ].join('\n');
    });

    return [
      '{',
      '  includeStandardComponents: true,',
      '  includeTheme: false,',
      '  customComponents: [',
      ...components,
      '  ],',
      `  title: ${JSON.stringify(schema.title ?? 'JSON Document Definition')},`,
      `  description: ${JSON.stringify(
        schema.description ?? 'Document definition with standard components'
      )},`,
      '}',
    ].join('\n');
  }

  private renderCustomSchemaDeclarations(
    customComponents: CustomComponentSchema[]
  ): string {
    if (customComponents.length === 0) return '';

    const declarations: string[] = [];
    customComponents.forEach((component, index) => {
      if (this.formatName === 'docx' || !component.versionedProps?.length) {
        declarations.push(
          `const ${this.customSchemaName(index)} = ${this.schemaToTypeBox(
            component.propsSchema
          )};`
        );
      }

      component.versionedProps?.forEach((entry, versionIndex) => {
        declarations.push(
          `const ${this.customSchemaName(index, versionIndex)} = ${this.schemaToTypeBox(
            entry.propsSchema
          )};`
        );
      });
    });

    return `\n${declarations.join('\n\n')}\n`;
  }

  private customSchemaName(
    componentIndex: number,
    versionIndex?: number
  ): string {
    return versionIndex === undefined
      ? `CustomComponent${componentIndex + 1}PropsSchema`
      : `CustomComponent${componentIndex + 1}Version${versionIndex + 1}PropsSchema`;
  }

  private schemaToTypeBox(schema: TSchema): string {
    const jsonSchema = JSON.parse(JSON.stringify(schema)) as JsonSchema;
    this.refRoot = jsonSchema;
    this.refStack = [];
    try {
      return this.jsonSchemaToTypeBox(jsonSchema, 0);
    } finally {
      this.refRoot = undefined;
      this.refStack = [];
    }
  }

  /**
   * Resolve a `$ref` against the root schema's `$defs` / `definitions`.
   *
   * TypeBox's `Type.Ref` takes a bare identifier and does not register its
   * target: emitting one would produce a file that throws "Unable to
   * dereference schema" the first time it is compiled or checked. Inlining the
   * target keeps the generated schema standalone and executable.
   */
  private resolveRef(pointer: string): JsonSchema {
    const containers = [this.refRoot?.$defs, this.refRoot?.definitions].filter(
      (container): container is JsonSchema => this.isJsonSchema(container)
    );
    const name = pointer.replace(/^#\/(?:\$defs|definitions)\//, '');

    for (const container of containers) {
      const direct = container[name];
      if (this.isJsonSchema(direct)) return direct;
    }
    for (const container of containers) {
      for (const candidate of Object.values(container)) {
        if (this.isJsonSchema(candidate) && candidate.$id === pointer) {
          return candidate;
        }
      }
    }

    throw new Error(
      `Cannot export schema: $ref "${pointer}" has no definition under $defs or definitions.`
    );
  }

  private withRefGuard(pointer: string, render: () => string): string {
    if (this.refStack.includes(pointer)) {
      throw new Error(
        `Cannot export schema: $ref "${pointer}" is recursive. Inlining it would not terminate — declare the schema with Type.Recursive instead.`
      );
    }
    this.refStack.push(pointer);
    try {
      return render();
    } finally {
      this.refStack.pop();
    }
  }

  private jsonSchemaToTypeBox(schema: JsonSchema, level: number): string {
    const options = this.schemaOptions(schema, level + 1);
    const withOptions = (expression: string): string => {
      if (!options) return expression;
      return expression.endsWith('()')
        ? `${expression.slice(0, -1)}${options})`
        : `${expression.slice(0, -1)}, ${options})`;
    };

    if ('$ref' in schema && typeof schema.$ref === 'string') {
      // Keywords sitting alongside $ref win over the target's own, matching
      // JSON Schema 2019-09 sibling semantics.
      const { $ref, ...siblings } = schema;
      const target = this.resolveRef($ref as string);
      return this.withRefGuard($ref as string, () =>
        this.jsonSchemaToTypeBox({ ...target, ...siblings }, level)
      );
    }

    if ('const' in schema) {
      return withOptions(`Type.Literal(${JSON.stringify(schema.const)})`);
    }

    if (Array.isArray(schema.anyOf)) {
      return this.renderSchemaArrayCall('Union', schema.anyOf, options, level);
    }

    if (Array.isArray(schema.oneOf)) {
      return this.renderSchemaArrayCall('Union', schema.oneOf, options, level);
    }

    if (Array.isArray(schema.allOf)) {
      return this.renderSchemaArrayCall(
        'Intersect',
        schema.allOf,
        options,
        level
      );
    }

    if (Array.isArray(schema.enum)) {
      const literals = schema.enum.map((value) => ({ const: value }));
      return this.renderSchemaArrayCall('Union', literals, options, level);
    }

    if (schema.not && this.isJsonSchema(schema.not)) {
      const inner = this.jsonSchemaToTypeBox(schema.not, level + 1);
      return options ? `Type.Not(${inner}, ${options})` : `Type.Not(${inner})`;
    }

    if (Array.isArray(schema.type)) {
      const variants = schema.type.map((type) => ({ type }));
      return this.renderSchemaArrayCall('Union', variants, options, level);
    }

    switch (schema.type) {
      case 'object':
        return this.renderObjectSchema(schema, options, level);
      case 'array': {
        const items = this.isJsonSchema(schema.items)
          ? this.jsonSchemaToTypeBox(schema.items, level + 1)
          : 'Type.Unknown()';
        return options
          ? `Type.Array(${items}, ${options})`
          : `Type.Array(${items})`;
      }
      case 'string':
        return withOptions('Type.String()');
      case 'integer':
        return withOptions('Type.Integer()');
      case 'number':
        return withOptions('Type.Number()');
      case 'boolean':
        return withOptions('Type.Boolean()');
      case 'null':
        return withOptions('Type.Null()');
      default:
        return options ? `Type.Unknown(${options})` : 'Type.Unknown()';
    }
  }

  private renderObjectSchema(
    schema: JsonSchema,
    options: string,
    level: number
  ): string {
    const properties = this.isJsonSchema(schema.properties)
      ? schema.properties
      : {};
    const patterns = this.isJsonSchema(schema.patternProperties)
      ? schema.patternProperties
      : {};

    if (
      Object.keys(properties).length === 0 &&
      Object.keys(patterns).length > 0
    ) {
      const [pattern, value] = Object.entries(patterns)[0];
      const valueSchema = this.isJsonSchema(value)
        ? this.jsonSchemaToTypeBox(value, level + 1)
        : 'Type.Unknown()';
      const keySchema = `Type.String({ pattern: ${JSON.stringify(pattern)} })`;
      return options
        ? `Type.Record(${keySchema}, ${valueSchema}, ${options})`
        : `Type.Record(${keySchema}, ${valueSchema})`;
    }

    const required = new Set(
      Array.isArray(schema.required)
        ? schema.required.filter(
            (value): value is string => typeof value === 'string'
          )
        : []
    );
    const propertyLines = Object.entries(properties).map(([key, value]) => {
      const valueSchema = this.isJsonSchema(value)
        ? this.jsonSchemaToTypeBox(value, level + 2)
        : 'Type.Unknown()';
      const expression = required.has(key)
        ? valueSchema
        : `Type.Optional(${valueSchema})`;
      return `${this.indent(level + 1)}${JSON.stringify(key)}: ${expression},`;
    });
    const objectLiteral = [
      '{',
      ...propertyLines,
      `${this.indent(level)}}`,
    ].join('\n');

    return options
      ? `Type.Object(${objectLiteral}, ${options})`
      : `Type.Object(${objectLiteral})`;
  }

  private renderSchemaArrayCall(
    method: 'Union' | 'Intersect',
    schemas: unknown[],
    options: string,
    level: number
  ): string {
    const entries = schemas.map((entry) =>
      this.isJsonSchema(entry)
        ? this.jsonSchemaToTypeBox(entry, level + 1)
        : 'Type.Unknown()'
    );
    const array = [
      '[',
      ...entries.map((entry) => `${this.indent(level + 1)}${entry},`),
      `${this.indent(level)}]`,
    ].join('\n');
    return options
      ? `Type.${method}(${array}, ${options})`
      : `Type.${method}(${array})`;
  }

  private schemaOptions(schema: JsonSchema, level: number): string {
    const options = Object.fromEntries(
      Object.entries(schema).filter(([key]) => !STRUCTURAL_KEYS.has(key))
    );

    if (Object.keys(options).length === 0) return '';
    return this.renderValue(options, level);
  }

  private renderValue(value: unknown, level: number): string {
    if (Array.isArray(value)) {
      if (value.length === 0) return '[]';
      return [
        '[',
        ...value.map(
          (entry) =>
            `${this.indent(level + 1)}${this.renderValue(entry, level + 1)},`
        ),
        `${this.indent(level)}]`,
      ].join('\n');
    }

    if (this.isJsonSchema(value)) {
      const entries = Object.entries(value);
      if (entries.length === 0) return '{}';
      return [
        '{',
        ...entries.map(
          ([key, entry]) =>
            `${this.indent(level + 1)}${JSON.stringify(key)}: ${this.renderValue(
              entry,
              level + 1
            )},`
        ),
        `${this.indent(level)}}`,
      ].join('\n');
    }

    return JSON.stringify(value);
  }

  private isJsonSchema(value: unknown): value is JsonSchema {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private indent(level: number): string {
    return '  '.repeat(level);
  }

  private toPascalCase(str: string): string {
    return str
      .split('-')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join('');
  }
}
